import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { openPayingCustomersModal } from '@/components/paying-customers-only-modal';
import { Button, Select, Loader, Alert, Checkbox } from '@mantine/core';
import { useCreatePayment, useUnpaidReservations, useGenerateAfipInvoice, useUpdatePayment } from '@/hooks/api-hooks';
import { Business, Payment } from '@turizi/shared';
import { DEFAULT_CURRENCY, PAYER_TYPES } from '@turizi/shared';
import BusinessSelector from '@/components/business-selector';
import { MoneyNumberInput, PaymentMethodSelect } from '@/components/snippets';
import { localTenant } from '@/services/session-service';
import { notify } from '@/lib/notify';
import { IconExternalLink, IconAlertCircle, IconArrowRight, IconBuildingStore } from '@tabler/icons-react';
import { ReservationToString } from '@/lib/models-utils';
import { multiWordSearch } from '@/utils/search-utils';
import CurrencySelector from './currency-selector';
import { isTier1Tenant } from '@/lib/format-utils';
import AfipInvoiceFields from './afip-invoice-fields';
import ReservationUnpaidRow from './reservation/reservation-unpaid-row';
import SearchInput from './search-input';
import DatePickerCustom from './custom-date-picker';
import PaymentRetentionsSection from './payment-retentions-section';

const EMPTY_PAYMENT: Partial<Payment> = {
    currency: DEFAULT_CURRENCY,
    payment_method: 'bank_transfer',
    attachments: [],
    retentions: [],
    retained_amount: 0,
    notes: '',
    status: 'paid',
};

export interface NewPaymentInModalProps {
    onClose: () => void;
    initialFromClient?: Business | null;
    initialToClient?: Business | null;
    initialAffectedReservations?: number[];
    initialAmount?: number;
    initialCurrency?: string;
    onSuccess?: (payment: Payment) => void;
}

export default function NewPaymentInModal({
    onClose,
    initialFromClient,
    initialToClient,
    initialAffectedReservations,
    initialAmount,
    initialCurrency,
    onSuccess,
}: NewPaymentInModalProps) {
    const tenant = localTenant();
    const { t } = useTranslation();

    useEffect(() => {
        if (!isTier1Tenant()) {
            openPayingCustomersModal();
            if (onClose) onClose();
        }
    }, [onClose]);

    const [savedPayment, setSavedPayment] = useState<Payment | null>(null);

    const defaultTenantBusiness = useMemo<Business | null>(() => {
        if (tenant?.businesses && tenant.businesses.length > 0) {
            return tenant.businesses[0];
        }
        if (tenant?.id && tenant?.name) {
            return {
                id: tenant.id,
                name: tenant.name,
                type: PAYER_TYPES.BUSINESS
            } as Business;
        }
        return null;
    }, [tenant]);

    const isTenantBusiness = (b: Business | null | undefined): boolean => {
        if (!b || !b.id) return false;
        if (defaultTenantBusiness && defaultTenantBusiness.id === b.id) return true;
        return !!tenant?.businesses?.some(tb => tb.id === b.id);
    };

    const [myCompany, setMyCompany] = useState<Business | null>(() => defaultTenantBusiness);

    const [client, setClient] = useState<Business | null>(() => {
        if (initialFromClient && !isTenantBusiness(initialFromClient)) return initialFromClient;
        if (initialToClient && !isTenantBusiness(initialToClient)) return initialToClient;
        return null;
    });

    const isSameOriginDestination = useMemo(() => {
        return !!client && !!myCompany && client.type === PAYER_TYPES.BUSINESS && client.id === myCompany.id;
    }, [client, myCompany]);

    const createMutation = useCreatePayment();
    const generateInvoiceMutation = useGenerateAfipInvoice();
    const updatePaymentMutation = useUpdatePayment();

    const [payment, setPayment] = useState<Partial<Payment>>(() => ({
        ...EMPTY_PAYMENT,
        amount: initialAmount ?? undefined,
        currency: initialCurrency ?? DEFAULT_CURRENCY,
    }));
    const [affectedReservations, setAffectedReservations] = useState<number[]>(initialAffectedReservations || []);
    const [reservationAmounts, setReservationAmounts] = useState<Record<number, number>>({});
    const [reservationsStatus, setReservationsStatus] = useState<Record<number, string>>(() => {
        const initialStatus: Record<number, string> = {};
        if (initialAffectedReservations) {
            initialAffectedReservations.forEach(id => {
                initialStatus[id] = 'paid';
            });
        }
        return initialStatus;
    });

    // Predictive auto-fill for single reservation link
    useEffect(() => {
        if (affectedReservations.length === 1) {
            const singleId = affectedReservations[0];
            const amt = Number(payment.amount);
            setReservationAmounts(prev => {
                if (prev[singleId] === amt) return prev;
                return { ...prev, [singleId]: amt };
            });
        }
    }, [affectedReservations, payment.amount]);

    const [generateTaxVoucher, setGenerateTaxVoucher] = useState(false);
    const [afipReceiverName, setAfipReceiverName] = useState('');
    const [afipDocTipo, setAfipDocTipo] = useState('99');
    const [afipDocNro, setAfipDocNro] = useState('0');
    const [afipInvoiceType, setAfipInvoiceType] = useState('6');
    const [afipPos, setAfipPos] = useState<number | null>(null);
    const [afipConcepto, setAfipConcepto] = useState('2');
    const [afipDescription, setAfipDescription] = useState('');
    const [afipNetAmount, setAfipNetAmount] = useState<string | number>('');
    const [afipIvaRate, setAfipIvaRate] = useState('5');
    const [afipIvaAmount, setAfipIvaAmount] = useState<number>(0);
    const [afipTotalAmount, setAfipTotalAmount] = useState<number>(0);

    const [afipFchServDesde, setAfipFchServDesde] = useState<Date | null>(new Date());
    const [afipFchServHasta, setAfipFchServHasta] = useState<Date | null>(new Date());
    const [afipFchVtoPago, setAfipFchVtoPago] = useState<Date | null>(new Date());
    const [afipTaxCondition, setAfipTaxCondition] = useState<string>('5');

    // Auto-update AFIP fields when client changes
    useEffect(() => {
        if (client) {
            setAfipReceiverName(client.name || '');
            const detectedTaxCondition = client.tax_condition || '5';
            setAfipDocTipo('80');
            setAfipDocNro(client.tax_id_number || '');
            setAfipTaxCondition(detectedTaxCondition);
            setAfipInvoiceType(detectedTaxCondition === '1' ? '1' : '6');
        }
    }, [client]);

    // Handle docTipo = 99 docNro reset
    useEffect(() => {
        if (afipDocTipo === '99') {
            setAfipDocNro('0');
            setAfipTaxCondition('5');
        } else if (afipDocNro === '0') {
            setAfipDocNro('');
        }
    }, [afipDocTipo]);

    // Auto-calculate Net / IVA / Total from payment amount
    useEffect(() => {
        const amt = Number(payment.amount);
        if (amt > 0) {
            if (afipInvoiceType === '11') {
                setAfipNetAmount(amt);
                setAfipIvaAmount(0);
                setAfipTotalAmount(amt);
            } else {
                let rate = 0.21;
                if (afipIvaRate === '4') rate = 0.105;
                if (afipIvaRate === '6') rate = 0.27;
                if (afipIvaRate === '3') rate = 0;

                const net = Math.round((amt / (1 + rate)) * 100) / 100;
                const iva = Math.round((amt - net) * 100) / 100;
                setAfipNetAmount(net);
                setAfipIvaAmount(iva);
                setAfipTotalAmount(amt);
            }
        } else {
            setAfipNetAmount('');
            setAfipIvaAmount(0);
            setAfipTotalAmount(0);
        }
    }, [payment.amount, afipIvaRate, afipInvoiceType]);

    const {
        data: reservationsResponse,
        isLoading: isLoadingReservations
    } = useUnpaidReservations(client?.id, client?.type, { enabled: !!client });
    const unpaidReservations = reservationsResponse?.data;

    const [reservationSearchQuery, setReservationSearchQuery] = useState('');

    const filteredUnpaidReservations = useMemo(() => {
        if (!unpaidReservations) return [];
        if (!reservationSearchQuery.trim()) return unpaidReservations;
        return unpaidReservations.filter(r => multiWordSearch(ReservationToString(r, t), reservationSearchQuery));
    }, [unpaidReservations, reservationSearchQuery, t]);

    const [prevClientId, setPrevClientId] = useState<number | undefined>(client?.id);
    if (client?.id !== prevClientId) {
        setPrevClientId(client?.id);
        setAffectedReservations([]);
        setReservationsStatus({});
        setReservationSearchQuery('');
    }

    // Automatically calculate and set amount & currency based on selected reservations (IN)
    useEffect(() => {
        if (!unpaidReservations || unpaidReservations.length === 0) return;

        const selected = unpaidReservations.filter(r => affectedReservations.includes(Number(r.id)));

        if (selected.length > 0) {
            const totalsByCurrency: Record<string, number> = {};
            for (const r of selected) {
                const priceMap = r.price_per_currency || {};
                for (const [curr, amt] of Object.entries(priceMap)) {
                    totalsByCurrency[curr] = (totalsByCurrency[curr] || 0) + Number(amt);
                }
            }
            const uniqueCurrencies = Object.keys(totalsByCurrency);
            let targetCurrency = payment.currency || DEFAULT_CURRENCY;

            if (uniqueCurrencies.length > 0 && !uniqueCurrencies.includes(targetCurrency)) {
                targetCurrency = uniqueCurrencies[0];
            }

            const total = totalsByCurrency[targetCurrency];

            setPayment(p => {
                if (p.currency === targetCurrency && p.amount === total) {
                    return p;
                }
                return {
                    ...p,
                    currency: targetCurrency,
                    amount: total > 0 ? total : undefined
                };
            });
        } else if (affectedReservations.length === 0) {
            setPayment(p => {
                if (p.amount === undefined) return p;
                return { ...p, amount: undefined };
            });
        }
    }, [affectedReservations, payment.currency, unpaidReservations]);

    // Automatically populate default assigned amounts for selected reservations (IN)
    useEffect(() => {
        if (!unpaidReservations || unpaidReservations.length === 0) return;
        setReservationAmounts(prev => {
            const next = { ...prev };
            let updated = false;
            unpaidReservations.forEach(r => {
                const rId = Number(r.id);
                if (affectedReservations.includes(rId) && next[rId] === undefined) {
                    const priceMap = r.price_per_currency || {};
                    const total = Object.values(priceMap).reduce((sum: number, amt: any) => sum + Number(amt || 0), 0);
                    if (total > 0) {
                        next[rId] = total;
                        updated = true;
                    }
                }
            });
            return updated ? next : prev;
        });
    }, [unpaidReservations, affectedReservations]);

    const handleSubmit = () => {
        const payer = client;
        const payee = myCompany;

        const performDbInsert = (attachmentsList: string[] = []) => {
            createMutation.mutate({
                ...payment,
                status: 'paid',
                expected_amount: null,
                amount: payment.amount,
                due_date: null,
                payment_date: payment.payment_date,
                from_person_id: payer?.type === PAYER_TYPES.PERSON ? payer.id : null,
                from_business_id: payer?.type === PAYER_TYPES.BUSINESS ? payer.id : null,
                to_person_id: payee?.type === PAYER_TYPES.PERSON ? payee.id : null,
                to_business_id: payee?.type === PAYER_TYPES.BUSINESS ? payee.id : null,
                attachments: attachmentsList,
                affected_reservations: affectedReservations.map(id => {
                    const reservation = unpaidReservations?.find(r => Number(r.id) === id);
                    const priceMap = reservation?.price_per_currency || {};
                    const totalPrice = Object.values(priceMap).reduce((sum: number, amt: any) => sum + Number(amt || 0), 0);
                    const fallbackAmt = totalPrice > 0 ? totalPrice : (affectedReservations.length === 1 ? Number(payment.amount || 0) : 0);
                    return {
                        id,
                        amount: reservationAmounts[id] !== undefined ? reservationAmounts[id] : fallbackAmt,
                        payment_status: reservationsStatus[id] || 'paid'
                    };
                }),
                affected_service_bookings: [],
            }, {
                onSuccess: ({ data: createdPayment }) => {
                    setSavedPayment(createdPayment);
                    if (onSuccess) onSuccess(createdPayment);
                }
            });
        };

        if (generateTaxVoucher && tenant?.id) {
            if (!afipPos || afipPos <= 0) {
                notify.error(t('pos_required_error'), t('error'));
                return;
            }
            const cleanDocNro = afipDocNro.replace(/\D/g, '');
            const data = {
                PtoVta: afipPos,
                CbteTipo: parseInt(afipInvoiceType, 10),
                Concepto: parseInt(afipConcepto, 10),
                description: afipDescription.trim() || undefined,
                DocTipo: parseInt(afipDocTipo, 10),
                DocNro: afipDocTipo === '99' ? 0 : parseInt(cleanDocNro, 10),
                CondicionIVAReceptorId: afipDocTipo === '99' ? 5 : parseInt(afipTaxCondition, 10) || 5,
                receiver_name: afipReceiverName || 'Consumidor Final',
                ImpTotal: afipTotalAmount,
                ImpNeto: typeof afipNetAmount === 'number' ? afipNetAmount : parseFloat(afipNetAmount) || afipTotalAmount,
                ImpIVA: afipInvoiceType === '11' ? 0 : afipIvaAmount,
                ivaId: afipInvoiceType === '11' ? null : parseInt(afipIvaRate, 10),
                FchServDesde: (afipConcepto === '2' || afipConcepto === '3') && afipFchServDesde ? afipFchServDesde.toISOString().split('T')[0] : undefined,
                FchServHasta: (afipConcepto === '2' || afipConcepto === '3') && afipFchServHasta ? afipFchServHasta.toISOString().split('T')[0] : undefined,
                FchVtoPago: (afipConcepto === '2' || afipConcepto === '3') && afipFchVtoPago ? afipFchVtoPago.toISOString().split('T')[0] : undefined,
                business_id: client?.id
            };

            generateInvoiceMutation.mutate({
                tenantId: tenant.id,
                data
            }, {
                onSuccess: (afipRes: any) => {
                    notify.success(t('invoice_generated_success'), t('success'));
                    const downloadUrl = `${import.meta.env.VITE_API_BASE_URL}/afip/vouchers/${afipRes.voucherId}/download`;
                    performDbInsert([downloadUrl]);
                },
                onError: (err: any) => {
                    const msg = err.response?.data?.message || err.message || t('error');
                    notify.error(`${t('invoice_generation_failed')}: ${msg}`, t('error'));
                }
            });
        } else {
            performDbInsert();
        }
    };

    const companies = tenant?.businesses || [];
    const hasMultipleCompanies = companies.length > 1;

    const canSave = !!payment.amount && !!payment.payment_date && !!client;

    const ourBusinessBlock = (
        <div className="flex-1 space-y-1">
            <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                {t('payee')}
            </div>
            {hasMultipleCompanies ? (
                <Select
                    data={companies.map(c => ({ value: String(c.id), label: c.name }))}
                    value={myCompany ? String(myCompany.id) : undefined}
                    onChange={(val) => {
                        const found = companies.find(c => String(c.id) === val);
                        if (found) setMyCompany(found);
                    }}
                    allowDeselect={false}
                    className="font-bold flex-1"
                    size="sm"
                    styles={{
                        input: {
                            height: '40px',
                            fontWeight: 'bold',
                            backgroundColor: '#f8fafc',
                            borderColor: '#e2e8f0',
                            borderRadius: '8px'
                        }
                    }}
                    leftSection={<IconBuildingStore size={18} className="text-gray-500" />}
                />
            ) : myCompany?.name ? (
                <div className="h-10 px-3 bg-gray-100 rounded-md font-bold text-sm text-gray-700 flex items-center gap-2">
                    <IconBuildingStore size={18} className="text-gray-500" />
                    <span>{myCompany.name}</span>
                </div>
            ) : (
                <Alert color="red" icon={<IconAlertCircle size={16} />} radius="md" p="xs" className="text-xs">
                    {t('no_tenant_business_error')}
                </Alert>
            )}
        </div>
    );

    const clientSelectorBlock = (
        <div className="flex-1 space-y-1">
            <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                {t('payer')}
            </div>
            <BusinessSelector
                placeholder={<div className='w-full text-sm text-gray-400 p-1'>{t('select_client')}</div>}
                onSelect={setClient}
                selected={client}
                className='font-bold'
            />
        </div>
    );

    return (
        <div className="w-[1000px] max-w-full mx-auto">
            <div className="mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                    {/* Left Column: Payment Details */}
                    <div className="space-y-4">
                        {/* Payer (Client) -> Payee (MyCompany) */}
                        <div className="flex gap-4 items-center p-4 bg-gray-50 border border-gray-200 rounded-xl">
                            {clientSelectorBlock}
                            <div className="flex items-center justify-center pt-4 z-10">
                                <div className="w-8 h-8 rounded-full bg-red-100 text-red-500 flex items-center justify-center font-bold shadow-sm">
                                    <IconArrowRight size={18} />
                                </div>
                            </div>
                            {ourBusinessBlock}
                        </div>

                        {isSameOriginDestination && (
                            <Alert color="red" icon={<IconAlertCircle size={16} />} radius="md">
                                {t('payer_payee_cannot_be_same')}
                            </Alert>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <CurrencySelector
                                label={t('currency')}
                                value={payment.currency}
                                onChange={(curr) => setPayment(p => ({ ...p, currency: curr }))}
                            />
                            <MoneyNumberInput
                                label={t('amount')}
                                amount={payment.amount}
                                onChange={(val) => setPayment(p => ({ ...p, amount: val }))}
                            />
                        </div>
                        <PaymentMethodSelect
                            value={payment.payment_method}
                            onChange={(method) => setPayment(p => ({ ...p, payment_method: method }))}
                        />
                        <DatePickerCustom
                            label={t('payment_date')}
                            value={payment.payment_date}
                            clearable={false}
                            onChange={([start, _end]) => setPayment(p => ({
                                ...p,
                                payment_date: start
                            }))}
                        />
                        <div className="p-3 rounded-xl border border-slate-200 bg-white">
                            <PaymentRetentionsSection
                                retentions={payment.retentions || []}
                                totalAmount={Number(payment.amount || 0)}
                                currency={payment.currency}
                                compact
                                onChange={(retentions, retainedAmount) =>
                                    setPayment(p => ({
                                        ...p,
                                        retentions,
                                        retained_amount: retainedAmount,
                                    }))
                                }
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {t('notes')}
                            </label>
                            <textarea
                                value={payment.notes || ''}
                                onChange={(e) => setPayment(p => ({ ...p, notes: e.target.value }))}
                                className="w-full text-sm border border-slate-200 rounded-md p-2 h-20 focus:outline-none focus:border-slate-400"
                            />
                        </div>

                        {generateTaxVoucher && (
                            <AfipInvoiceFields
                                receiverName={afipReceiverName}
                                setReceiverName={setAfipReceiverName}
                                docTipo={afipDocTipo}
                                setDocTipo={setAfipDocTipo}
                                docNro={afipDocNro}
                                setDocNro={setAfipDocNro}
                                invoiceType={afipInvoiceType}
                                setInvoiceType={setAfipInvoiceType}
                                pos={afipPos}
                                setPos={setAfipPos}
                                concepto={afipConcepto}
                                setConcepto={setAfipConcepto}
                                description={afipDescription}
                                setDescription={setAfipDescription}
                                netAmount={afipNetAmount}
                                ivaRate={afipIvaRate}
                                setIvaRate={setAfipIvaRate}
                                ivaAmount={afipIvaAmount}
                                totalAmount={afipTotalAmount}
                                fchServDesde={afipFchServDesde}
                                setFchServDesde={setAfipFchServDesde}
                                fchServHasta={afipFchServHasta}
                                setFchServHasta={setAfipFchServHasta}
                                fchVtoPago={afipFchVtoPago}
                                setFchVtoPago={setAfipFchVtoPago}
                                taxCondition={afipTaxCondition}
                                setTaxCondition={setAfipTaxCondition}
                            />
                        )}
                    </div>

                    {/* Right Column: Unpaid Reservations (Client Receivables) */}
                    <div className="space-y-4">
                        <div className='solid-box-border-tinted w-full max-h-[485px] overflow-y-auto p-3'>
                            <div className="font-semibold text-sm mb-2 text-gray-700 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {t('unpaid_reservations')} {isLoadingReservations && <Loader size="xs" color='black' />}
                                </div>
                                {unpaidReservations && unpaidReservations.length > 0 && (
                                    <Checkbox
                                        size="xs"
                                        label={t('select_all')}
                                        checked={affectedReservations.length === unpaidReservations.length}
                                        onChange={(e) => {
                                            if (e.currentTarget.checked) {
                                                setAffectedReservations(unpaidReservations.map(r => Number(r.id)));
                                                const amounts: Record<number, number> = {};
                                                const status: Record<number, string> = {};
                                                unpaidReservations.forEach(r => {
                                                    const priceMap = r.price_per_currency || {};
                                                    const total = Object.values(priceMap).reduce((sum: number, amt: any) => sum + Number(amt || 0), 0);
                                                    amounts[Number(r.id)] = total;
                                                    status[Number(r.id)] = 'paid';
                                                });
                                                setReservationAmounts(amounts);
                                                setReservationsStatus(status);
                                            } else {
                                                setAffectedReservations([]);
                                                setReservationAmounts({});
                                                setReservationsStatus({});
                                            }
                                        }}
                                    />
                                )}
                            </div>

                            {unpaidReservations && unpaidReservations.length > 0 && (
                                <SearchInput
                                    onSearch={(value) => setReservationSearchQuery(value)}
                                    placeholder={t('search')}
                                    size="xs"
                                    initialValue={reservationSearchQuery}
                                    className="w-full mb-2"
                                />
                            )}

                            {!unpaidReservations || unpaidReservations.length === 0 ? (
                                <div className="text-xs text-slate-400 italic text-center py-6">
                                    {t('no_unpaid_reservations_found')}
                                </div>
                            ) : filteredUnpaidReservations.length === 0 ? (
                                <div className="text-xs text-slate-400 italic text-center py-6">
                                    {t('no_results')}
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {filteredUnpaidReservations.map(reservation => {
                                        const id = Number(reservation.id);
                                        const isSelected = affectedReservations.includes(id);
                                        const priceMap = reservation.price_per_currency || {};
                                        const totalAmt = Object.values(priceMap).reduce((sum: number, amt: any) => sum + Number(amt || 0), 0);
                                        const appliedAmt = reservationAmounts[id] !== undefined ? reservationAmounts[id] : totalAmt;

                                        return (
                                            <ReservationUnpaidRow
                                                key={id}
                                                reservation={reservation}
                                                isSelected={isSelected}
                                                onSelectChange={(selected) => {
                                                    if (selected) {
                                                        setAffectedReservations(prev => [...prev, id]);
                                                        setReservationAmounts(prev => ({ ...prev, [id]: totalAmt }));
                                                        setReservationsStatus(prev => ({ ...prev, [id]: 'paid' }));
                                                    } else {
                                                        setAffectedReservations(prev => prev.filter(x => x !== id));
                                                        setReservationAmounts(prev => {
                                                            const copy = { ...prev };
                                                            delete copy[id];
                                                            return copy;
                                                        });
                                                        setReservationsStatus(prev => {
                                                            const copy = { ...prev };
                                                            delete copy[id];
                                                            return copy;
                                                        });
                                                    }
                                                }}
                                                appliedAmount={appliedAmt}
                                                onAmountChange={(val) => {
                                                    setReservationAmounts(prev => ({ ...prev, [id]: val }));
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex items-center justify-end gap-6 mt-6 pt-4 border-t border-gray-100">
                    <Button
                        onClick={onClose}
                        color="black"
                        size="xs"
                        variant='subtle'
                    >
                        {t('close')}
                    </Button>
                    {savedPayment ? (
                        <Link
                            to={`/payments/${savedPayment.id}`}
                            className='text-xs font-semibold'
                        >
                            <Button
                                color="blue"
                                size="xs"
                                variant='subtle'
                                onClick={onClose}
                                rightSection={<IconExternalLink size={12} />}
                            >
                                {t('view_payment')}
                            </Button>
                        </Link>
                    ) : (
                        <Button
                            onClick={handleSubmit}
                            loading={createMutation.isPending || generateInvoiceMutation.isPending || updatePaymentMutation.isPending}
                            color="black"
                            size="xs"
                            disabled={!canSave}
                        >
                            {t('save')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
